'use client';

import type { CmsImageDimensions } from '@/cms/image-source';
import { Skeleton } from '@/components/ui/feedback/status/skeleton';
import type { ImageLoadingState } from '@/components/ui/media/wsrv-image';
import { cn } from '@/lib/ui/utils';
import { useCallback, useRef, useState } from 'react';
import {
  CmsImage,
  type CmsImageProps,
  type CmsImageVariant,
} from './cms-image';

export type CmsMasonryImageItem = {
  key: string;
  src: string;
  alt: string;
  dimensions?: CmsImageDimensions;
};

function CmsMasonryImage({
  image,
  itemClassName,
  imageClassName,
  fallbackAspectRatio,
  sizes,
  variant,
  fetchPriority,
  onImageSettled,
}: {
  image: CmsMasonryImageItem;
  itemClassName?: string;
  imageClassName?: string;
  fallbackAspectRatio?: string;
  sizes: string;
  variant: CmsImageVariant;
  fetchPriority?: CmsImageProps['fetchPriority'];
  onImageSettled?: (image: CmsMasonryImageItem) => void;
}) {
  const [loadingState, setLoadingState] =
    useState<ImageLoadingState>('loading');
  const settledRef = useRef(false);
  const dimensions = image.dimensions;
  const aspectRatio = dimensions
    ? `${dimensions.width} / ${dimensions.height}`
    : fallbackAspectRatio;
  const handleLoadingStateChange = useCallback(
    (state: ImageLoadingState) => {
      setLoadingState(state);
      if (state === 'loading') {
        settledRef.current = false;
        return;
      }
      if (settledRef.current) {
        return;
      }

      settledRef.current = true;
      onImageSettled?.(image);
    },
    [image, onImageSettled]
  );
  const failed = loadingState === 'error';
  const loading = loadingState === 'loading';

  return (
    <div
      className={cn('cms-masonry-image-grid__item relative', itemClassName)}
      data-cms-image-state={loadingState}
      data-cms-image-fallback={dimensions ? 'false' : 'true'}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {loading ? (
        <Skeleton
          aria-hidden="true"
          className="absolute inset-0 size-full rounded-control"
        />
      ) : null}
      {failed ? null : (
        <CmsImage
          src={image.src}
          alt={image.alt}
          variant={variant}
          fetchPriority={fetchPriority}
          sizes={sizes}
          {...(dimensions ?? {})}
          className={cn(imageClassName, loading && 'opacity-0')}
          onLoadingStateChange={handleLoadingStateChange}
        />
      )}
    </div>
  );
}

export function CmsMasonryImageGrid({
  images,
  className,
  itemClassName,
  imageClassName,
  fallbackAspectRatio,
  sizes,
  variant,
  fetchPriority,
  onImageSettled,
}: {
  images: CmsMasonryImageItem[];
  className?: string;
  itemClassName?: string;
  imageClassName?: string;
  fallbackAspectRatio?: string;
  sizes: string;
  variant: CmsImageVariant;
  fetchPriority?: CmsImageProps['fetchPriority'];
  onImageSettled?: (image: CmsMasonryImageItem) => void;
}) {
  return (
    <div className={cn('cms-masonry-image-grid', className)}>
      {images.map((image) => (
        <CmsMasonryImage
          key={image.key}
          image={image}
          itemClassName={itemClassName}
          imageClassName={imageClassName}
          fallbackAspectRatio={fallbackAspectRatio}
          sizes={sizes}
          variant={variant}
          fetchPriority={fetchPriority}
          onImageSettled={onImageSettled}
        />
      ))}
    </div>
  );
}
